// Schedule's map (pages/ux-full schedule/map): a day's visits drawn from their addresses' points, each
// technician's route in their pen, and a pin opening the visit. It is drawn from the points alone, as an
// SVG: no tiles, no map library and no map service, so no address or point leaves Flowology. The visits
// whose address has no point are MapUnplaced's list, beside it, never dropped.
// DayMap (Domain/Schedule; was components/DayMap.tsx, BL-189) reads only --daymap-*; Link reads its own.
import type { CSSProperties, KeyboardEvent } from 'react';
import type { DayMapProps } from '../../schedule-schemas.ts';
import { Link } from '../Link/Link.tsx';
import { penStyle as pens, type Pen } from '../pen.ts';
import './DayMap.css';

/** A pin's or a route's pen; none (nobody, or waiting) is red (--daymap-bad). */
const penStyle = (pen: number | null | undefined): CSSProperties =>
  pens('daymap', (pen ?? null) as Pen | null) ?? ({ '--daymap-pen': 'var(--daymap-bad)' } as CSSProperties);

// The drawing's box, in its own units (the prototype's 1000 wide). Its height follows the points' shape.
const W = 1000;
const PAD = 90;
const H_MIN = 520;
const H_MAX = 860;
/** The least ground the map covers, so visits a street apart don't sit across it. */
const MIN_SPAN_KM = 2;
const KM_PER_DEGREE = 111.32;
/** A pin's own room: pins closer than this sit in a ring around their middle. */
const PIN = 52;
const SCALES_KM = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100, 200];

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface PlacedPin {
  id: string;
  x: number;
  y: number;
  /** Where its label goes, from the pin; far: a leader line joins them. */
  label: { x: number; y: number; far: boolean; lx: number; ly: number };
}
export interface MapLayout {
  width: number;
  height: number;
  pins: PlacedPin[];
  routes: { id: string; pen: number | null; points: [number, number][] }[];
  areas: { name: string; x: number; y: number }[];
  /** The shop's square, its name placed as a pin's label is. */
  shop: { x: number; y: number; label: string; at: PlacedPin['label'] } | null;
  /** The ground one grid square covers, and its length in the drawing. */
  scale: { km: number; length: number; words: string };
  /** The grid's lines, a scale's length apart. */
  grid: { xs: number[]; ys: number[] };
}

const hit = (a: Box, b: Box) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/**
 * Where everything goes: the points projected into the box that fits them (equirectangular about their
 * middle, true to scale at that latitude), pins too close together in a ring, each label where it
 * overlaps no other label, pin, area name or the shop (right, left, above, below, then further out).
 */
export function mapLayout(d: DayMapProps): MapLayout {
  const all = [
    ...d.pins.map((p) => [p.lat, p.lng] as const),
    ...(d.shop ? [[d.shop.lat, d.shop.lng] as const] : []),
  ];
  const lats = all.map(([a]) => a);
  const lngs = all.map(([, b]) => b);
  const lat0 = all.length ? (Math.min(...lats) + Math.max(...lats)) / 2 : 0;
  const lng0 = all.length ? (Math.min(...lngs) + Math.max(...lngs)) / 2 : 0;
  const k = Math.cos((lat0 * Math.PI) / 180);
  // kilometres east and south of the middle
  const km = (lat: number, lng: number): [number, number] => [
    (lng - lng0) * KM_PER_DEGREE * k,
    (lat0 - lat) * KM_PER_DEGREE,
  ];
  const pts = all.map(([a, b]) => km(a, b));
  const xs = pts.map(([x]) => x);
  const ys = pts.map(([, y]) => y);
  const spanX = Math.max(all.length ? Math.max(...xs) - Math.min(...xs) : 0, MIN_SPAN_KM);
  const spanY = Math.max(all.length ? Math.max(...ys) - Math.min(...ys) : 0, MIN_SPAN_KM);
  const s = Math.min((W - 2 * PAD) / spanX, (H_MAX - 2 * PAD) / spanY);
  const height = Math.round(clamp(spanY * s + 2 * PAD, H_MIN, H_MAX));
  const cx = all.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : 0;
  const cy = all.length ? (Math.min(...ys) + Math.max(...ys)) / 2 : 0;
  const to = ([x, y]: [number, number]): [number, number] => [
    W / 2 + (x - cx) * s,
    height / 2 + (y - cy) * s,
  ];

  // pins one pin's room apart or closer share a ring around their middle, in the day's order
  const raw = d.pins.map((p) => to(km(p.lat, p.lng)));
  const groups: number[][] = [];
  raw.forEach(([x, y], i) => {
    const g = groups.find((m) => Math.hypot(raw[m[0]!]![0] - x, raw[m[0]!]![1] - y) < PIN);
    if (g) g.push(i);
    else groups.push([i]);
  });
  const at: [number, number][] = [...raw];
  for (const g of groups) {
    if (g.length < 2) continue;
    const mx = g.reduce((t, i) => t + raw[i]![0], 0) / g.length;
    const my = g.reduce((t, i) => t + raw[i]![1], 0) / g.length;
    const r = Math.max(Math.min(4 + g.length, 8) * 10, (g.length * PIN) / (2 * Math.PI));
    g.forEach((i, n) => {
      const t = (n / g.length) * Math.PI * 2 - Math.PI / 2;
      at[i] = [
        clamp(mx + r * Math.cos(t), PIN / 2, W - PIN / 2),
        clamp(my + r * Math.sin(t), PIN / 2, height - PIN / 2),
      ];
    });
  }

  // a grid square a round distance across, about a fifth of the box; its length is the scale bar
  const want = W / 5 / s;
  const unit = [...SCALES_KM].reverse().find((u) => u <= want) ?? SCALES_KM[0]!;
  const length = unit * s;
  const grid = {
    xs: Array.from(
      { length: Math.ceil(W / length) + 1 },
      (_, i) => W / 2 + (i - Math.ceil(W / length / 2)) * length,
    ).filter((x) => x > 0 && x < W),
    ys: Array.from(
      { length: Math.ceil(height / length) + 1 },
      (_, i) => height / 2 + (i - Math.ceil(height / length / 2)) * length,
    ).filter((y) => y > 0 && y < height),
  };

  // each town named above its visits, else below them, where it covers no pin and no town named before
  // it, with a little room either side; a town with no such place goes unnamed (its pins say where they are)
  const areas: MapLayout['areas'] = [];
  const areaBoxes: Box[] = [];
  const pinBoxes = at.map(([x, y]) => ({ x: x - PIN / 2, y: y - PIN / 2, w: PIN, h: PIN }));
  const byArea = new Map<string, number[]>();
  d.pins.forEach((p, i) => p.area && byArea.set(p.area, [...(byArea.get(p.area) ?? []), i]));
  for (const [name, idx] of byArea) {
    const x = clamp(
      idx.reduce((t, i) => t + at[i]![0], 0) / idx.length,
      name.length * 6,
      W - name.length * 6,
    );
    const ys = idx.map((i) => at[i]![1]);
    const free = [Math.min(...ys) - 56, Math.max(...ys) + 66]
      .filter((y) => y >= 28 && y <= height - 8)
      .map((y) => ({ y, box: { x: x - name.length * 6 - 8, y: y - 22, w: name.length * 12 + 16, h: 26 } }))
      .find(({ box }) => ![...areaBoxes, ...pinBoxes].some((b) => hit(b, box)));
    if (!free) continue;
    const { y, box } = free;
    areaBoxes.push(box);
    areas.push({ name, x, y });
  }

  const shopAt = d.shop ? to(km(d.shop.lat, d.shop.lng)) : null;
  const own = (q: { x: number; y: number }): Box => ({ x: q.x - PIN / 2, y: q.y - PIN / 2, w: PIN, h: PIN });
  const taken: Box[] = [
    ...areaBoxes,
    ...at.map(([x, y]) => own({ x, y })),
    ...(shopAt ? [own({ x: shopAt[0], y: shopAt[1] })] : []),
    { x: 16, y: height - 60, w: length + 24, h: 52 },
  ];
  const place = (q: { x: number; y: number }, label: string) => {
    const w = label.length * 10.5 + 6;
    const h = 22;
    const tries = [
      [32, -h / 2],
      [-32 - w, -h / 2],
      [-w / 2, -34 - h],
      [-w / 2, 34],
      [32, -h / 2 - 28],
      [32, -h / 2 + 28],
      [-32 - w, -h / 2 - 28],
      [-32 - w, -h / 2 + 28],
      [-w / 2, -64 - h],
      [-w / 2, 64],
    ] as const;
    const mine = own(q);
    let best: Box | null = null;
    let fewest = Infinity;
    for (const [dx, dy] of tries) {
      const box = { x: q.x + dx, y: q.y + dy, w, h };
      if (box.x < 0 || box.y < 0 || box.x + w > W || box.y + h > height) continue;
      const n = taken.filter((t) => !(t.x === mine.x && t.y === mine.y) && hit(box, t)).length;
      if (n < fewest) {
        best = box;
        fewest = n;
      }
      if (!n) break;
    }
    const b = best ?? { x: q.x + 32, y: q.y - h / 2, w, h };
    taken.push(b);
    const gap = Math.hypot(Math.max(b.x - q.x, q.x - (b.x + w), 0), Math.max(b.y - q.y, q.y - (b.y + h), 0));
    return {
      x: b.x - q.x + 3,
      y: b.y - q.y + h - 5,
      far: gap > 48,
      lx: Math.min(Math.max(0, b.x - q.x), b.x + w - q.x),
      ly: Math.min(Math.max(0, b.y - q.y), b.y + h - q.y),
    };
  };
  // the shop's name first: it is one place, where a pin's label can go round it
  const shop =
    d.shop && shopAt
      ? {
          x: shopAt[0],
          y: shopAt[1],
          label: d.shop.label,
          at: place({ x: shopAt[0], y: shopAt[1] }, d.shop.label),
        }
      : null;
  const pins = d.pins.map((p, i) => {
    const [x, y] = at[i]!;
    return { id: p.id, x, y, label: place({ x, y }, p.label) };
  });
  const where = new Map(pins.map((p) => [p.id, [p.x, p.y] as [number, number]]));
  return {
    width: W,
    height,
    pins,
    routes: d.routes.map((r) => ({
      id: r.id,
      pen: r.pen,
      points: r.stops.map((id) => where.get(id)).filter((p): p is [number, number] => !!p),
    })),
    areas,
    shop,
    scale: { km: unit, length, words: unit < 1 ? `${Math.round(unit * 1000)} m` : `${unit} km` },
    grid,
  };
}

const r1 = (n: number) => Math.round(n * 10) / 10;

/**
 * The day's visits on the map. A pin is a visit, in its lead technician's pen: filled while they're on
 * site, an outline when booked, muted when done, red and dashed when it's late, at risk or nobody has it.
 * A pin is a button (Enter or Space too) when the screen passes onPick.
 */
export function DayMap({
  data,
  selected,
  onPick,
  none = 'None of the day’s visits has a location for its address.',
}: {
  data: DayMapProps;
  selected?: string | null;
  onPick?: (id: string) => void;
  /** With no pin to draw, the sentence said where the map would be (dashed rules above and below). */
  none?: string;
}) {
  if (!data.pins.length) return <p className="fl-daymap__none">{none}</p>;
  const m = mapLayout(data);
  const byId = new Map(data.pins.map((p) => [p.id, p]));
  const key = (id: string) => (e: KeyboardEvent<SVGGElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onPick?.(id);
  };
  // the chosen pin last, so nothing is drawn over it
  const order = [...m.pins].sort((a, b) => Number(a.id === selected) - Number(b.id === selected));
  return (
    <figure className="flw-map">
      {/* on a phone the drawing keeps a readable size and scrolls inside its section, as a table does */}
      <div
        className="flw-map-scroll"
        {...(onPick ? {} : { role: 'region', tabIndex: 0, 'aria-label': 'The map, scrolls sideways' })}
      >
        <svg
          viewBox={`0 0 ${m.width} ${m.height}`}
          role="group"
          aria-label={`The day’s visits on the map: ${data.pins.length} ${data.pins.length === 1 ? 'visit' : 'visits'}`}
        >
          <rect className="flw-map-bg" x="0" y="0" width={m.width} height={m.height} />
          <g className="flw-map-grid" aria-hidden="true">
            {m.grid.xs.map((x) => (
              <line key={`x${x}`} x1={r1(x)} y1="0" x2={r1(x)} y2={m.height} />
            ))}
            {m.grid.ys.map((y) => (
              <line key={`y${y}`} x1="0" y1={r1(y)} x2={m.width} y2={r1(y)} />
            ))}
          </g>
          {m.routes.map(
            (r) =>
              r.points.length > 1 && (
                <polyline
                  key={r.id}
                  className="flw-map-route"
                  style={penStyle(r.pen)}
                  points={r.points.map(([x, y]) => `${r1(x)},${r1(y)}`).join(' ')}
                  aria-hidden="true"
                />
              ),
          )}
          {/* town names over the routes, haloed, so a line never runs through one */}
          {m.areas.map((a) => (
            <text key={a.name} className="flw-map-area" x={r1(a.x)} y={r1(a.y)} aria-hidden="true">
              {a.name}
            </text>
          ))}
          {m.shop && (
            <g className="flw-map-shop" transform={`translate(${r1(m.shop.x)} ${r1(m.shop.y)})`}>
              <rect x="-12" y="-12" width="24" height="24" />
              {m.shop.at.far && (
                <line className="flw-map-lead" x1="0" y1="0" x2={r1(m.shop.at.lx)} y2={r1(m.shop.at.ly)} />
              )}
              <text x={r1(m.shop.at.x)} y={r1(m.shop.at.y)}>
                {m.shop.label}
              </text>
            </g>
          )}
          {order.map((q) => {
            const p = byId.get(q.id)!;
            const on = q.id === selected;
            return (
              <g
                key={q.id}
                className={`flw-map-pin flw-map-${p.state}${p.late ? ' flw-map-late' : ''}${on ? ' flw-map-on' : ''}`}
                style={penStyle(p.state === 'waiting' ? null : p.pen)}
                transform={`translate(${r1(q.x)} ${r1(q.y)})`}
                {...(onPick
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      'aria-pressed': on,
                      onClick: () => onPick(q.id),
                      onKeyDown: key(q.id),
                    }
                  : { role: 'img' })}
                aria-label={p.title}
              >
                <circle r={p.state === 'now' ? 24 : 18} />
                {q.label.far && (
                  <line className="flw-map-lead" x1="0" y1="0" x2={r1(q.label.lx)} y2={r1(q.label.ly)} />
                )}
                <text x={r1(q.label.x)} y={r1(q.label.y)}>
                  {p.label}
                </text>
              </g>
            );
          })}
          <g className="flw-map-scale" transform={`translate(24 ${m.height - 24})`} aria-hidden="true">
            <line x1="0" y1="0" x2={r1(m.scale.length)} y2="0" />
            <line x1="0" y1="-8" x2="0" y2="0" />
            <line x1={r1(m.scale.length)} y1="-8" x2={r1(m.scale.length)} y2="0" />
            <text x="0" y="-16">
              {m.scale.words}
            </text>
          </g>
        </svg>
      </div>
      <figcaption className="flw-map-key">
        A filled pin is working now, an outline is booked, a muted one is done, red is at risk or has no one
        yet. A dashed line is a technician’s route, in their pen; a grid square is {m.scale.words} across.
      </figcaption>
    </figure>
  );
}

/**
 * The day's visits whose address has no point, so the map can't place them: each with when and who, under
 * the map on a hairline, "Not on the map" and how many over them. The one shown has a heavier rule down
 * its side; the others a Show it (onPick).
 */
export function MapUnplaced({
  data,
  selected,
  onPick,
}: {
  data: Pick<DayMapProps, 'unplaced'>;
  selected?: string | null;
  onPick?: (id: string) => void;
}) {
  if (!data.unplaced.length) return <p className="fl-daymap__sub">Every visit today is on the map.</p>;
  return (
    <div className="fl-daymap__unplaced" role="group" aria-label="Not on the map">
      <h3 className="fl-daymap__unplaced-h">
        <b>Not on the map</b> {data.unplaced.length}
      </h3>
      <p className="fl-daymap__sub">No location for these addresses, so they aren’t on the map.</p>
      <ul className="fl-daymap__rows">
        {data.unplaced.map((u) => (
          <li key={u.id} className="fl-daymap__row" data-on={u.id === selected ? '' : undefined}>
            <span>
              <b>{u.title}</b>
              <span className="fl-daymap__sub">{u.line}</span>
              <span className="fl-daymap__sub">{u.address}</span>
            </span>
            {u.id === selected ? (
              <span className="fl-daymap__shown">Shown</span>
            ) : onPick ? (
              <Link onPress={() => onPick(u.id)} label={`Show the visit for ${u.title}`}>
                Show it
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
